import styled from 'styled-components';

const colors = {
  background: '#f1f3f5',
  white: '#ffffff',
  border: '#dde1e6',
  borderSoft: '#e7eaee',
  header: '#f7f8f9',
  label: '#f6f7f8',
  text: '#2f3744',
  muted: '#5d6675',
  navy: '#243346',
  green: '#14b866',
  greenSoft: '#d0f0df'
};

export const Container = styled.div`
  min-height: 100%;
  padding: 24px 32px 80px;
  background: ${colors.background};
  color: ${colors.text};
  font-size: 14px;
`;

export const PageTitle = styled.h1`
  margin: 0 0 34px;
  color: #20242a;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
`;

export const ServiceSelect = styled.select`
  width: 270px;
  height: 44px;
  padding: 0 42px 0 18px;
  border: 1px solid ${colors.border};
  border-radius: 0;
  background: ${colors.white};
  color: ${colors.text};
  font-size: 16px;
`;

export const ServiceRadioGroup = styled.div`
  display: inline-flex;
  align-items: center;
  margin-bottom: 10px;
`;

export const ServiceRadioLabel = styled.label<{ checked: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 72px;
  height: 36px;
  padding: 0 18px;
  border: 1px solid ${({ checked }) => (checked ? colors.navy : colors.border)};
  background: ${({ checked }) => (checked ? colors.navy : colors.white)};
  color: ${({ checked }) => (checked ? colors.white : colors.navy)};
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  z-index: ${({ checked }) => (checked ? 1 : 0)};
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;

  /* Segmented control: share borders between adjacent segments */
  & + & {
    margin-left: -1px;
  }

  /* Round only the outer corners */
  &:first-of-type {
    border-radius: 4px 0 0 4px;
  }
  &:last-of-type {
    border-radius: 0 4px 4px 0;
  }

  &:hover {
    color: ${({ checked }) => (checked ? colors.white : colors.navy)};
  }

  input {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    border: 0;
    clip: rect(0 0 0 0);
    overflow: hidden;
    white-space: nowrap;
  }
`;

export const ListTablePanel = styled.div`
  min-height: 980px;
  border: 1px solid ${colors.border};
  background: ${colors.white};
  overflow: auto;
`;

export const ListTable = styled.table`
  width: 100%;
  /* Columns sum to 1500px so the full set fits on a 1920px viewport
     (available content area ≈ 1614px after 240px sider + container/panel paddings). */
  min-width: 1500px;
  border-collapse: collapse;
  table-layout: fixed;

  th,
  td {
    padding: 16px 12px;
    border-bottom: 1px solid ${colors.borderSoft};
    text-align: left;
    vertical-align: middle;
    color: ${colors.text};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  th {
    background: ${colors.header};
    font-size: 14px;
    font-weight: 700;
    word-break: keep-all;
  }

  td {
    font-size: 12px;
    line-height: 1.35;
  }

  tbody tr {
    cursor: pointer;
  }

  tbody tr:hover {
    background: #fbfcfd;
  }

  /* 계약 번호 */
  th:nth-child(1),
  td:nth-child(1) {
    width: 80px;
  }

  /* 상호명 */
  th:nth-child(2),
  td:nth-child(2) {
    width: 200px;
  }

  /* 사업자 번호 */
  th:nth-child(3),
  td:nth-child(3) {
    width: 130px;
  }

  /* 대표자 명, 서비스 */
  th:nth-child(4),
  td:nth-child(4),
  th:nth-child(5),
  td:nth-child(5) {
    width: 90px;
  }

  /* 청구 범위 */
  th:nth-child(6),
  td:nth-child(6) {
    width: 300px;
  }

  /* 세금계산서 수신 이메일 1, 2 */
  th:nth-child(7),
  td:nth-child(7),
  th:nth-child(8),
  td:nth-child(8) {
    width: 180px;
  }

  /* 입금계좌 */
  th:nth-child(9),
  td:nth-child(9) {
    width: 160px;
  }

  /* 입금자 명 */
  th:nth-child(10),
  td:nth-child(10) {
    width: 120px;
  }
`;

export const DetailHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const SectionTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 18px;
  color: #1f242c;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;

  &::before {
    content: '';
    width: 4px;
    height: 18px;
    border-radius: 2px;
    background: ${colors.green};
  }
`;

export const BackButton = styled.button`
  min-width: 70px;
  height: 32px;
  border: 1px solid ${colors.greenSoft};
  border-radius: 4px;
  background: ${colors.white};
  color: ${colors.green};
  font-size: 14px;
  cursor: pointer;
`;

export const DetailTable = styled.table`
  width: 100%;
  margin-bottom: 22px;
  border-collapse: collapse;
  table-layout: fixed;
  background: ${colors.white};

  th,
  td {
    height: 48px;
    padding: 12px 14px;
    border-bottom: 1px solid ${colors.borderSoft};
    color: ${colors.text};
    line-height: 1.45;
    text-align: left;
    vertical-align: middle;
  }
`;

export const SectionHeaderCell = styled.td`
  height: 54px !important;
  background: ${colors.header};
  color: ${colors.green} !important;
  font-weight: 700;
`;

export const AccountHeaderCell = SectionHeaderCell;

export const LabelCell = styled.td`
  width: 180px;
  background: ${colors.label};
  font-weight: 700;
`;

export const ValueCell = styled.td`
  background: ${colors.white};
  color: ${colors.muted};
`;

export const TextButton = styled.button`
  float: right;
  min-width: 88px;
  height: 30px;
  border: 1px solid ${colors.greenSoft};
  border-radius: 4px;
  background: ${colors.white};
  color: ${colors.green};
  font-size: 14px;
  cursor: pointer;
`;

export const BusinessNotice = styled.p`
  margin: 0 0 14px 10px;
  color: ${colors.text};
  line-height: 1.5;

  a {
    color: ${colors.text};
    text-decoration: underline;
  }
`;
